/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/**
 * Parf mobile storefront hotfix.
 * Runs after the document has been parsed, so these rules override the
 * late inline mobile search styles in theme.liquid.
 */
(() => {
  const STYLE_ID = 'parf-mobile-runtime-hotfix';
  const MOBILE_CSS = `
    @media screen and (max-width: 990px) {
      .section-header header.header > form.search.search-modal__form {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }

      body.parf-mobile-menu-open .section-header details-modal.header__search,
      body.parf-mobile-menu-open .section-header .search-modal,
      body.parf-mobile-menu-open .section-header .search-modal__content,
      body.parf-mobile-menu-open .section-header .search-modal__form {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }

      body.parf-mobile-menu-open #MainContent {
        position: relative !important;
        z-index: 0 !important;
      }

      body.parf-mobile-menu-open #MainContent .field,
      body.parf-mobile-menu-open #MainContent input.search__input.field__input,
      body.parf-mobile-menu-open #MainContent .field__label,
      body.parf-mobile-menu-open #MainContent .search-modal__form {
        z-index: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }

      .section-header,
      .shopify-section-header-sticky,
      sticky-header,
      .header-wrapper {
        z-index: 500 !important;
      }

      #Details-menu-drawer-container[open] .menu-drawer,
      #Details-menu-drawer-container.menu-opening .menu-drawer {
        z-index: 1000 !important;
      }

      body.parf-mobile-menu-open .slideshow__controls,
      body.parf-mobile-menu-open slideshow-component .slider-buttons {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    }

    @media screen and (max-width: 749px) {
      slideshow-component .slideshow.banner--adapt_image {
        display: flex !important;
        align-items: stretch !important;
        width: 100% !important;
        height: clamp(185px, 56vw, 320px) !important;
        min-height: clamp(185px, 56vw, 320px) !important;
        overflow: hidden !important;
      }

      slideshow-component .slideshow.banner--adapt_image .slideshow__slide {
        position: relative !important;
        flex: 0 0 100% !important;
        width: 100% !important;
        height: 100% !important;
        min-height: clamp(185px, 56vw, 320px) !important;
      }

      slideshow-component .slideshow.banner--adapt_image .slideshow__media-link,
      slideshow-component .slideshow.banner--adapt_image .slideshow__media {
        display: block !important;
        position: absolute !important;
        inset: 0 !important;
        width: 100% !important;
        height: 100% !important;
        min-height: 100% !important;
      }

      slideshow-component .slideshow.banner--adapt_image .slideshow__media img,
      slideshow-component .slideshow.banner--adapt_image .banner__media img {
        display: block !important;
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
        object-position: center !important;
      }

      slideshow-component .slideshow__controls {
        bottom: 18px !important;
      }
    }
  `;

  function installStyles() {
    let style = document.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }
    style.textContent = MOBILE_CSS;
  }

  function hideDuplicateHeaderSearch() {
    document
      .querySelectorAll('.section-header header.header > form.search.search-modal__form')
      .forEach((form) => {
        form.hidden = true;
        form.setAttribute('aria-hidden', 'true');
        form.style.setProperty('display', 'none', 'important');
      });
  }

  function closeHeaderSearch() {
    document
      .querySelectorAll('.section-header details-modal.header__search > details[open]')
      .forEach((details) => details.removeAttribute('open'));
  }

  function syncMenuState() {
    const drawerDetails = document.getElementById('Details-menu-drawer-container');
    const open = Boolean(
      drawerDetails &&
      (drawerDetails.open || drawerDetails.classList.contains('menu-opening'))
    );
    document.body.classList.toggle('parf-mobile-menu-open', open);
    if (open) closeHeaderSearch();
  }

  function initialiseMobileFix() {
    installStyles();
    hideDuplicateHeaderSearch();
    syncMenuState();

    const drawerDetails = document.getElementById('Details-menu-drawer-container');
    if (drawerDetails) {
      const observer = new MutationObserver(syncMenuState);
      observer.observe(drawerDetails, {
        attributes: true,
        attributeFilter: ['open', 'class'],
      });
      drawerDetails.addEventListener('toggle', syncMenuState);
    }

    document.addEventListener('shopify:section:load', () => {
      hideDuplicateHeaderSearch();
      syncMenuState();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseMobileFix, { once: true });
  } else {
    initialiseMobileFix();
  }
})();
